// Bucket ("bodega") model for tótems.
//
// A tótem's `status` is one of 11 ENUM values, but operators think in terms of 5
// physical buckets. This module is the single source of truth mapping statuses to
// buckets and, for warehouse buckets, the canonical status a bulk move targets.
//
// Kept in sync with the groupings shown in TotemStatsCards.tsx.
import { AT_CLIENT_STATUSES } from "~/services/totemService";
import { Database } from "~/types/supabase";

type TotemStatus = Database["public"]["Enums"]["totem_status"];

export type TotemBucketKey =
  | "disponible"
  | "en_cliente"
  | "en_transito"
  | "lavado"
  | "con_problema";

export interface TotemBucket {
  key: TotemBucketKey;
  label: string;
  // All statuses that count toward this bucket.
  statuses: TotemStatus[];
  // The status a bulk move INTO this bucket writes. `null` for "En Cliente",
  // which requires picking a client and is driven by facturas/guías, not bulk moves.
  target: TotemStatus | null;
  // Note stamped on each totem_movement when moving into this bucket.
  moveNote?: string;
}

export const TOTEM_BUCKETS: TotemBucket[] = [
  {
    key: "disponible",
    label: "Disponible",
    statuses: ["available", "filled"],
    target: "available",
    moveNote: "Movido a disponible",
  },
  {
    key: "en_cliente",
    label: "En Cliente",
    statuses: AT_CLIENT_STATUSES,
    target: null,
  },
  {
    key: "en_transito",
    label: "Uso Interno",
    statuses: ["in_transit", "pickup_transit"],
    target: "pickup_transit",
    moveNote: "Movido a uso interno",
  },
  {
    key: "lavado",
    label: "Lavado",
    statuses: ["returned", "cleaning"],
    target: "cleaning",
    moveNote: "Movido a lavado",
  },
  {
    key: "con_problema",
    label: "Con Problema",
    statuses: ["damaged", "lost"],
    target: "damaged",
    moveNote: "Movido a con problema",
  },
];

// Warehouse buckets = every bucket that can be a bulk-move destination (all but "En Cliente").
export const WAREHOUSE_BUCKET_KEYS: TotemBucketKey[] = TOTEM_BUCKETS.filter(
  (b) => b.target !== null
).map((b) => b.key);

const STATUS_TO_BUCKET = new Map<TotemStatus, TotemBucketKey>(
  TOTEM_BUCKETS.flatMap((b) => b.statuses.map((s) => [s, b.key] as const))
);

export function getBucketForStatus(status: TotemStatus): TotemBucketKey {
  // Every ENUM value is covered by TOTEM_BUCKETS; fall back to "disponible" defensively.
  return STATUS_TO_BUCKET.get(status) ?? "disponible";
}

export function getBucket(key: TotemBucketKey): TotemBucket {
  return TOTEM_BUCKETS.find((b) => b.key === key)!;
}
